import { useEffect, useState, useCallback } from 'react';
import browser from '@openclassrooms/utils/browser';
import { getCookies } from 'Modules/cookies';
import getMarketingStaticContent from '../constants/marketingStaticContents';
import getBlueshiftLiveContent from '../services/getBlueshiftLiveContent';
import { COOKIE_NAME, SlotNames } from '../constants/blueshift';
import type { SlotNamesToContent } from '..';
import getMaintenanceStaticContent, {
  SHOULD_DISPLAY_MAINTENANCE_BANNER,
} from '../constants/maintenanceStaticContent';

export default function useBlueshiftLiveContent<T extends SlotNames>(slot: T) {
  const [liveContent, setLiveContent] = useState<SlotNamesToContent[T] | null>(null);
  const { href } = browser.window.location;

  const matchUrl = useCallback(
    (matchWithRegExp, urlToMatch) =>
      matchWithRegExp ? href.match(new RegExp(urlToMatch)) : href.includes(urlToMatch),
    [href]
  );

  const loadSlotLiveContent = useCallback(async () => {
    if (!getCookies()[COOKIE_NAME]) {
      setTimeout(loadSlotLiveContent, 100);
      return;
    }

    let slotContent: SlotNamesToContent[T];
    const { data: slotLiveContentData } = (await getBlueshiftLiveContent(slot)) || {};

    if (!slotLiveContentData || !slotLiveContentData.content) {
      return;
    }

    if (slotLiveContentData.show === false) {
      return;
    }

    const { content } = slotLiveContentData;

    if (content.matchingUrl || content.isMatchingUrlRegExp) {
      slotContent = matchUrl(content.isMatchingUrlRegExp, content.matchingUrl)
        ? slotLiveContentData
        : null;
    } else {
      slotContent = slotLiveContentData;
    }

    if (!slotContent) {
      return;
    }

    setLiveContent(slotContent);
  }, [setLiveContent, slot, matchUrl]);

  const loadSlotStaticContent = useCallback(() => {
    const slotStaticData = getMarketingStaticContent(slot);
    const { slotDatas } = slotStaticData;
    let slotContent: SlotNamesToContent[T] | undefined;

    if (slotDatas.length > 1) {
      slotContent = slotDatas.find(({ content }) =>
        matchUrl(content.isMatchingUrlRegExp, content.matchingUrl)
      );
    } else {
      [slotContent] = slotDatas;
    }

    if (SHOULD_DISPLAY_MAINTENANCE_BANNER) {
      const slotMaintenanceData = getMaintenanceStaticContent(slot);

      const { slotDatas: maintenanceSlotDatas } = slotMaintenanceData;

      if (maintenanceSlotDatas.length > 1) {
        slotContent = maintenanceSlotDatas.find(({ content }) =>
          matchUrl(content.isMatchingUrlRegExp, content.matchingUrl)
        );
      } else if (maintenanceSlotDatas.length === 1) {
        [slotContent] = maintenanceSlotDatas;
      }
    }

    if (!slotContent || slotContent?.show === false) {
      setLiveContent(null);
      return;
    }

    setLiveContent(slotContent);
  }, [setLiveContent, slot, matchUrl]);

  useEffect(() => {
    if (!SHOULD_DISPLAY_MAINTENANCE_BANNER) {
      loadSlotLiveContent();
    }
    loadSlotStaticContent();
  }, [loadSlotLiveContent, loadSlotStaticContent]);

  return { liveContent };
}
